// components
import { Form, Input, Select, DatePicker, Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { Head } from "components/molecules/Head/Head";
import { useOfferUpdatePage } from "containers/OfferUpdate/useOfferUpdatePage";
import Label from "components/atoms/Label/Label";
import Button from "components/atoms/Button/Button";
import Spinner from "components/atoms/Spinner/Spinner";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import getIcon from "utils/getIcon";

// styles
import styles from "containers/OfferUpdate/offerUpdate.module.css";

// constants
import { DateTimeFormats, PageTitles } from "constants/global";
import dayjs from "dayjs";

// Types
interface ActiveVoucherList {
	acquired_date: string;
	end_date: string;
	offer_id: string;
	start_date: string;
	voucher_internal_title: string;
	voucher_no: string;
	voucher_type_code: string;
	voucher_type_name: string;
}

const OfferUpdatePage = () => {
	const {
		cardNumber,
		handleCardNumber,
		form,
		codeCountError,
		handleRemarks,
		offer,
		offerLabel,
		fetchOfferData,
		handleOffer,
		offerSelect,
		foundOffers,
		startDate,
		endDate,
		updateOfferSubmit,
		findOfferSubmit,
		goBack,
		remarks,
		loading,
		editOffer,
		resetFields,
		editOfferData,
		handleCheckboxChange,
		checkedItems,
		editTitle,
		editSubtitle,
		handleStartDate,
		handleEndDate,
		selectedItems,
		foundButtonFindOffersEnable,
		unEditFoundOffers,
		areAllItemsSelected
	} = useOfferUpdatePage();

	// Handle select all checkbox change
	const handleSelectAllChange = (e: CheckboxChangeEvent) => {
		handleCheckboxChange(
			{
				target: {
					checked: e.target.checked,
					"data-props": null
				}
			},
			true
		);
	};

	// Handle individual checkbox change
	const handleIndividualCheckboxChange = (e: CheckboxChangeEvent, item: ActiveVoucherList) => {
		handleCheckboxChange({
			target: {
				checked: e.target.checked,
				"data-props": item
			}
		});
	};

	return (
		<div className="min-h-screen relative">
			{loading && <Spinner type="transparent" />}
			<PageTitle showBackIcon={editOffer} title={editTitle} subTitle={editSubtitle} goBack={goBack} />
			<div className={styles?.root}>
				<Head>{PageTitles.OFFER_UPDATE}</Head>

				<Form form={form} requiredMark={false} layout="vertical">
					{!editOffer ? (
						<div className="grid grid-cols-2 mt-6">
							<Form.Item
								className="mb-auto "
								label={<Label requiredMark label="CR Card Number" classes={{ label: styles?.basic_input_label }} />}
								name="cardNumber"
								initialValue={""}
								extra={codeCountError && !cardNumber && <ErrorMessage classes={styles?.err_message} message="This field is required" />}
							>
								<Input
									className={`${styles?.input_field}  ${cardNumber ? styles?.filled : ""}`}
									placeholder=""
									disabled={foundOffers}
									value={cardNumber}
									onChange={(e) => handleCardNumber(e.target.value as any)}
								/>
							</Form.Item>
						</div>
					) : (
						<></>
					)}
					{foundButtonFindOffersEnable ? (
						<div className={` grid grid-cols-2 place-items-end mt-6`}>
							<Button label="Find Offers" onClick={findOfferSubmit} />
						</div>
					) : (
						<></>
					)}

					{unEditFoundOffers ? (
						<div className=" grid grid-cols-2 mt-6">
							<Form.Item
								className="mb-auto"
								name="offer"
								initialValue={""}
								// extra={codeCountError && !offer && <ErrorMessage classes={styles?.err_message} message="This field is required" />}
								label={<Label label="Offer" classes={{ label: styles?.basic_input_label }} />}
							>
								<Select
									className={offer ? styles?.active_select_input : styles?.selectInput}
									value={offer}
									bordered={false}
									style={{ textAlign: "center" }}
									onChange={(e) => handleOffer(e)}
									suffixIcon={getIcon({ icon: "caretDropDown" })}
									options={offerSelect}
									showSearch
									optionFilterProp="label"
									filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
								></Select>
							</Form.Item>
						</div>
					) : (
						<></>
					)}

					{unEditFoundOffers ? (
						<div className="grid grid-cols-4 ">
							<div className="grid col-span-3">
								<div className="grid grid-cols-2  mt-6 font-bold text-base">{`User: ${cardNumber} Offer : ${offerLabel || ""}`}</div>
								<div className="grid grid-cols-12  mt-6 rounded-lg border-2">
									<div className={styles.headerTable}>
										{/* Select All Checkbox */}
										{fetchOfferData && fetchOfferData.length > 0 && <Checkbox checked={areAllItemsSelected()} onChange={handleSelectAllChange} />}
									</div>
									<div className={`${styles.headerTable} col-span-4`}>Name</div>
									<div className={`${styles.headerTable} col-span-3`}>Acquired Date</div>
									<div className={`${styles.headerTable} col-span-2`}>Start</div>
									<div className={`${styles.headerTable} col-span-2`}>End</div>
								</div>

								{
									<div>
										{fetchOfferData?.map((item: ActiveVoucherList, index: number) => {
											return (
												<div key={index} className="grid grid-cols-12 border-2 rounded-lg mt-2">
													<div className={styles.bodyTable}>
														<Checkbox checked={checkedItems[item.voucher_no] || false} onChange={(e) => handleIndividualCheckboxChange(e, item)} />
													</div>
													<div className={`${styles.bodyTable} col-span-4`}>{item?.voucher_type_name}</div>
													<div className={`${styles.bodyTable} col-span-3`}>{item?.acquired_date}</div>
													<div className={`${styles.bodyTable} col-span-2`}>{item?.start_date}</div>
													<div className={`${styles.bodyTable} col-span-2`}>{item?.end_date}</div>
												</div>
											);
										})}
									</div>
								}
								{!fetchOfferData || fetchOfferData.length <= 0 ? <div className="mt-6 text-bold text-base text-center">No Offers Found </div> : <></>}
								<div className="grid grid-cols-6 place-items-end">
									<div className={`grid ${fetchOfferData !== undefined && fetchOfferData.length > 0 ? "col-span-5" : "col-span-6"} mt-6 button-button_wrapper`}>
										<Button label="Clear all" background="yellow" onClick={resetFields} />
									</div>

									{fetchOfferData !== undefined && fetchOfferData.length > 0 ? (
										<div className={` grid mt-6 button-button_wrapper`}>
											<Button label="Edit" onClick={editOfferData} />
										</div>
									) : (
										<></>
									)}
								</div>
							</div>
						</div>
					) : (
						<></>
					)}
					{editOffer ? (
						<>
							<div className="grid grid-cols-2  mt-6 font-bold text-base">{`User: ${cardNumber}`}</div>
							<div className="grid grid-cols-2  mt-6 font-bold text-base">{`Offer(s): `}</div>
							<div className="grid grid-cols-6  mt-6 font-bold  text-sm">
								<div className={styles.dataHeader}>Name</div>
								<div className={styles.dataHeader}>Acquired Date</div>
								<div className={styles.dataHeader}>Start</div>
								<div className={styles.dataHeader}>End</div>
								<div className="grid col-span-2"></div>
							</div>

							{selectedItems?.map((item: ActiveVoucherList, index: number) => {
								return (
									<div key={index} className="grid grid-cols-6 font-bold text-sm">
										<div className={styles.dataTable}>{item?.voucher_type_name}</div>
										<div className={styles.dataTable}>{item?.acquired_date}</div>
										<div className={styles.dataTable}>{item?.start_date}</div>
										<div className={styles.dataTable}>{item?.end_date}</div>
										<div className="grid col-span-2"></div>
									</div>
								);
							})}
							<div className=" grid grid-cols-2 mt-6">
								<Form.Item
									label={<Label label="Start Date" classes={{ label: styles?.basic_input_label }} requiredMark />}
									name="startDate"
									extra={codeCountError && !startDate && <ErrorMessage classes={styles?.err_message} message="This field is required" />}
								>
									<DatePicker
										data-testid="Start Date"
										allowClear={false}
										className={`${styles?.date_picker}`}
										style={{ width: "100%" }}
										placeholder="Start date"
										onChange={(_, dateString) => {
											handleStartDate(dateString);
										}}
										suffixIcon={getIcon({ icon: "calender" })}
										format={DateTimeFormats.USE_YYYY_MM_DD}
									/>
								</Form.Item>
							</div>
							<div className=" grid grid-cols-2 mt-6">
								<Form.Item
									extra={codeCountError && !endDate && <ErrorMessage classes={styles?.err_message} message="This field is required" />}
									label={<Label label="End Date" classes={{ label: styles?.basic_input_label }} requiredMark />}
									name="endDate"
								>
									<DatePicker
										data-testid="End Date"
										style={{ width: "100%" }}
										allowClear={false}
										className={`${styles?.date_picker}`}
										placeholder="End Date"
										onChange={(_, dateString) => {
											handleEndDate(dateString);
										}}
										suffixIcon={getIcon({ icon: "calender" })}
										format={DateTimeFormats.USE_YYYY_MM_DD}
										disabledDate={(current) => {
											return current && dayjs(current as any).isBefore(dayjs(startDate), "day");
										}}
									/>
								</Form.Item>
							</div>
							<div className="grid grid-cols-2">
								<Form.Item
									className="mb-0 mt-6"
									label={<Label label="Remarks" classes={{ label: styles?.basic_input_label }} requiredMark />}
									name="remarks"
									extra={codeCountError && !remarks && <ErrorMessage classes={styles?.err_message} message="This field is required" />}
								>
									<Input.TextArea
										value={remarks}
										data-testid="1"
										placeholder=""
										onChange={(e) => handleRemarks(e.target.value as any)}
										// className={`${fieldErrors?.reason || !inputFields?.reason ? styles?.basic_textarea_error : styles?.basic_textarea_active}`}
										rows={4}
										style={{ width: "100%", borderRadius: "0.5rem", border: "2px solid #dfe1e6" }}
									/>
								</Form.Item>
							</div>
							<div className=" grid grid-cols-2 place-items-end mt-6">
								<Button label="Update Offer" /* type={isButtonEnabled ? "activeLong" : "disabledLong"}*/ onClick={updateOfferSubmit} />
							</div>
						</>
					) : (
						<></>
					)}
				</Form>
			</div>
		</div>
	);
};

export default OfferUpdatePage;
